import React, { useState, useEffect } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/theme/monokai.css';
import 'codemirror/mode/python/python';
import { getCookie } from './auth/cookies';
import FileExplorerModal from './fileexplorer';
import { toast } from 'react-toastify';
import axios from 'axios';
import Markdown from 'react-markdown';
import scriptExample from './scriptexample';
import { 
  FaCode, 
  FaSave, 
  FaPlay, 
  FaDownload, 
  FaUndo, 
  FaFolder, 
  FaInfoCircle, 
  FaCog,
  FaList,
  FaTerminal,
  FaTachometerAlt,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaHistory,
  FaClock,
  FaArrowRight,
  FaLaptopCode
} from 'react-icons/fa';

function Trainer() {
    // State management
    const [value, setValue] = useState(scriptExample());
    const [queueStatus, setQueueStatus] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [logOutput, setLogOutput] = useState([]);
    const [environment, setEnvironment] = useState('numpy==2.2.0\npandas==2.2.3\nscikit-learn==1.6.0');
    const [method, setMethod] = useState('train');
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [spawnerStatus, setSpawnerStatus] = useState([]);
    const [userFiles, setUserFiles] = useState([]);
    const [showExplorer, setShowExplorer] = useState(false);
    const [latestLog, setLatestLog] = useState([]);
    const [lastestExecutionStatus, setLastestExecutionStatus] = useState(null);
    const [activeTab, setActiveTab] = useState('code');
    const [showConfirmReset, setShowConfirmReset] = useState(false);

    // CodeMirror options
    const options = {
        lineWrapping: true,
        lint: true,
        mode: 'python',
        lineNumbers: true,
        theme: isDarkMode ? 'monokai' : 'material',
        autoCloseTags: true,
        autoCloseBrackets: true,
    };

    // Fetch script on component mount
    useEffect(() => {
        axios.get('api/get_team_script/', { headers: { 'X-CSRFToken': getCookie('csrftoken') } })
            .then((response) => {
                if (response.data.script) {
                    setValue(response.data.script);
                }
                if (response.data.requirements) {
                    setEnvironment(response.data.requirements);
                }
            })
            .catch((error) => {
                console.error('Error fetching team script:', error);
                toast.error('Failed to load your script. Please try again later.');
            });
    }, []);

    // Detect system-wide dark mode changes
    useEffect(() => {
        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(darkModeMediaQuery.matches);
        const handleChange = (e) => setIsDarkMode(e.matches);
        darkModeMediaQuery.addEventListener('change', handleChange);

        return () => {
            darkModeMediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    // Fetch spawner status
    useEffect(() => {
        const fetchSpawnerStatus = () => {
            axios.get('api/get_spawner_status/', { headers: { 'X-CSRFToken': getCookie('csrftoken') } })
                .then((response) => {
                    setSpawnerStatus(response.data.status || []);
                })
                .catch((error) => {
                    console.error('Error fetching spawner status:', error);
                });
        };

        fetchSpawnerStatus();
        const interval = setInterval(fetchSpawnerStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    // Fetch queue status
    useEffect(() => {
        const fetchQueueStatus = () => {
            axios.get('api/get_submition_queue/', { headers: { 'X-CSRFToken': getCookie('csrftoken') } })
                .then((response) => {
                    setQueueStatus(response.data.queue || 0);
                })
                .catch((error) => {
                    console.error('Error fetching queue status:', error);
                });
        };

        fetchQueueStatus();
        const interval = setInterval(fetchQueueStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    // Fetch latest log
    useEffect(() => {
        const fetchLatestLog = () => {
            axios.get('api/get_latest_log/', { headers: { 'X-CSRFToken': getCookie('csrftoken') } })
                .then((response) => {
                    setLatestLog(response.data.log || []);
                })
                .catch((error) => {
                    console.error('Error fetching latest log:', error);
                });
        };

        fetchLatestLog();
        const interval = setInterval(fetchLatestLog, 5000);
        return () => clearInterval(interval);
    }, []);

    // Fetch latest execution status
    useEffect(() => {
        const fetchLatestExecutionStatus = () => {
            axios.get('api/get_latest_execution_status/', { headers: { 'X-CSRFToken': getCookie('csrftoken') } })
                .then((response) => {
                    setLastestExecutionStatus(response.data.task);
                })
                .catch((error) => {
                    console.error('Error fetching latest execution status:', error);
                });
        };

        fetchLatestExecutionStatus();
        const interval = setInterval(fetchLatestExecutionStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    // Save script
    const saveScript = async () => {
        setIsSaving(true);
        const data = {
            'script': value,
            'requirements': environment
        };
        
        try {
            const response = await axios.post(
                'api/save_team_script/', 
                data, 
                { headers: { 'X-CSRFToken': getCookie('csrftoken') } }
            );
            toast.success(response.data.status);
        } catch (error) {
            console.error('Error saving team script:', error);
            toast.error(error.response?.data?.error || 'Failed to save script');
        } finally {
            setIsSaving(false);
        }
    };

    // Submit job
    const handleSubmit = async () => {
        if (!method.trim()) {
            toast.warning('Please specify a method name to run');
            return;
        }
        
        setIsSubmitting(true);
        const data = {
            'script': value, 
            'requirements': environment,
            'method_name': method
        };
        
        try {
            const response = await axios.post(
                'api/submit_job/', 
                data, 
                { headers: { 'X-CSRFToken': getCookie('csrftoken') } }
            );
            toast.success(response.data.status);
            // Switch to logs tab to see output
            setActiveTab('logs');
        } catch (error) {
            console.error('Error submitting job:', error);
            toast.error(error.response?.data?.error || 'Failed to submit job');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Fetch user files
    const fetchUserFiles = () => {
        axios.get('api/user_files/', { headers: { 'X-CSRFToken': getCookie('csrftoken') } })
            .then((response) => {
                console.log(response.data.files);
                openFileExplorer(response.data.files || []);
            })
            .catch((error) => {
                console.error('Error fetching user files:', error);
                toast.error('Failed to load your files. Please try again.');
            });
    };

    // Open file explorer
    const openFileExplorer = (files) => {
        setUserFiles(files);
        setShowExplorer(true);
    };

    // Close file explorer
    const closeFileExplorer = () => {
        setShowExplorer(false);
    };

    // Download script
    const downloadScript = () => {
        const element = document.createElement("a");
        const file = new Blob([value], {type: 'text/plain'});
        element.href = URL.createObjectURL(file);
        element.download = "script.py";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        toast.info('Script downloaded successfully');
    };

    // Reset script
    const resetScript = () => {
        setShowConfirmReset(true);
    };

    // Confirm reset
    const confirmReset = () => {
        setValue(scriptExample());
        setEnvironment('numpy==2.2.0\npandas==2.2.3\nscikit-learn==1.6.0');
        setMethod('train');
        setShowConfirmReset(false);
        toast.info('Script has been reset to the default example');
    };

    // Get spawner status class
    const getSpawnerStatusClass = (status) => {
        return status === 'idle' 
            ? 'bg-green-100 dark:bg-green-900/20 border-green-400 dark:border-green-700' 
            : 'bg-red-100 dark:bg-red-900/20 border-red-400 dark:border-red-700';
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'failed':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            case 'running':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 min-h-screen pt-20 text-gray-900 dark:text-gray-200">
            {/* Header */}
            <header className="bg-gradient-to-r from-secondary to-secondary-dark text-white py-8">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold flex items-center">
                        <FaCode className="mr-3" />
                        Python Code Trainer
                    </h1>
                    <p className="mt-2 text-blue-100">
                        Write, test, and execute your Python code with dedicated GPU resources
                    </p>
                </div>
            </header>
    
            {/* Main Content */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tabs Navigation */}
                <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                    <ul className="flex flex-wrap -mb-px">
                        <li className="mr-2">
                            <button
                                className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                    activeTab === 'code'
                                    ? 'border-secondary text-secondary dark:text-secondary-light'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                                onClick={() => setActiveTab('code')}
                            >
                                <FaCode className="mr-2" />
                                Code Editor
                            </button>
                        </li>
                        <li className="mr-2">
                            <button
                                className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                    activeTab === 'status'
                                    ? 'border-secondary text-secondary dark:text-secondary-light'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                                onClick={() => setActiveTab('status')}
                            >
                                <FaTachometerAlt className="mr-2" />
                                Execution Status
                            </button>
                        </li>
                        <li className="mr-2">
                            <button
                                className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                    activeTab === 'logs'
                                    ? 'border-secondary text-secondary dark:text-secondary-light'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                                onClick={() => setActiveTab('logs')}
                            >
                                <FaTerminal className="mr-2" />
                                Logs
                            </button>
                        </li>
                        <li className="mr-2">
                            <button
                                className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                    activeTab === 'help'
                                    ? 'border-secondary text-secondary dark:text-secondary-light'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                                onClick={() => setActiveTab('help')}
                            >
                                <FaInfoCircle className="mr-2" />
                                Documentation
                            </button>
                        </li>
                    </ul>
                </div>
    
                {/* Code Editor Tab */}
                {activeTab === 'code' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            {/* Python Script Section */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
                                <div className="bg-secondary-dark p-4 flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaCode className="mr-2" />
                                        Python Code
                                    </h2>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={saveScript}
                                            disabled={isSaving}
                                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center text-sm"
                                        >
                                            {isSaving ? <FaSpinner className="animate-spin mr-1" /> : <FaSave className="mr-1" />}
                                            {isSaving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={downloadScript}
                                            className="px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center text-sm"
                                        >
                                            <FaDownload className="mr-1" />
                                            Download
                                        </button>
                                        <button
                                            onClick={resetScript}
                                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center text-sm"
                                        >
                                            <FaUndo className="mr-1" />
                                            Reset
                                        </button>
                                    </div>
                                </div>
                                <div className="p-0">
                                    <CodeMirror
                                        key="singleEditor"
                                        value={value}
                                        options={options}
                                        onBeforeChange={(editor, data, value) => setValue(value)}
                                        editorDidMount={(editor) => editor.setSize(null, '700px')}
                                    />
                                </div>
                            </div>
                            
                            {/* Method Selection and Run Section */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaPlay className="mr-2" />
                                        Execution
                                    </h2>
                                </div>
                                <div className="p-6">
                                    <div className="mb-4">
                                        <label htmlFor="method" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Method Name to Run
                                        </label>
                                        <div className="relative rounded-md shadow-sm">
                                            <input
                                                id="method"
                                                type="text"
                                                value={method}
                                                onChange={(e) => setMethod(e.target.value)}
                                                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-secondary focus:border-secondary"
                                                placeholder="e.g. train, predict, process_data"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            Specify the function name from your script that should be executed
                                        </p>
                                    </div>
                                    
                                    <button
                                        className="w-full px-4 py-2 bg-secondary hover:bg-secondary-dark text-white rounded-md shadow transition-colors duration-200 flex items-center justify-center"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <FaSpinner className="animate-spin mr-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaPlay className="mr-2" />
                                                Run Code
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Environment Configuration Section */}
                        <div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaCog className="mr-2" />
                                        Environment
                                    </h2>
                                </div>
                                <div className="p-6">
                                    <label htmlFor="environment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Python Package Requirements
                                    </label>
                                    <textarea
                                        id="environment"
                                        className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:outline-none focus:ring-secondary focus:border-secondary"
                                        value={environment}
                                        onChange={(e) => setEnvironment(e.target.value)}
                                        placeholder="numpy==1.25.0&#10;pandas==2.2.3&#10;scikit-learn==1.6.0"
                                    ></textarea>
                                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                        List one package per line in the format: package==version
                                    </p>
                                </div>
                            </div>
                            
                            {/* Spawner Status Section */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaTachometerAlt className="mr-2" />
                                        Spawner Status
                                    </h2>
                                </div>
                                <div className="p-6">
                                    {spawnerStatus.length === 0 ? (
                                        <div className="text-center py-4">
                                            <FaSpinner className="animate-spin h-8 w-8 mx-auto text-gray-400" />
                                            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading spawner status...</p>
                                        </div>
                                    ) : (
                                        <ul className="grid grid-cols-2 gap-3">
                                            {spawnerStatus.map((spawner, index) => (
                                                <li
                                                    key={index}
                                                    className={`p-3 rounded-lg border ${getSpawnerStatusClass(spawner.status)}`}
                                                >
                                                    <p className="text-gray-800 dark:text-gray-200 font-medium">Spawner {index + 1}</p>
                                                    <div className={`flex items-center mt-1 text-sm font-semibold ${
                                                        spawner.status === 'idle' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                                                    }`}>
                                                        {spawner.status === 'idle' ? (
                                                            <>
                                                                <FaCheck className="mr-1" />
                                                                Available
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaSpinner className="animate-spin mr-1" />
                                                                Busy
                                                            </>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                            
                            {/* File Explorer Section */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaFolder className="mr-2" />
                                        Your Files
                                    </h2>
                                </div>
                                <div className="p-6 text-center">
                                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                                        Access files generated by your script executions
                                    </p>
                                    <button
                                        onClick={fetchUserFiles}
                                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md shadow transition-colors duration-200 flex items-center justify-center mx-auto"
                                    >
                                        <FaFolder className="mr-2" />
                                        Open File Explorer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Status Tab */}
                {activeTab === 'status' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            {/* Latest Execution Status */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaHistory className="mr-2" />
                                        Latest Execution
                                    </h2>
                                </div>
                                
                                {lastestExecutionStatus ? (
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Task ID</p>
                                                <p className="text-lg font-mono">{lastestExecutionStatus.id || 'N/A'}</p>
                                            </div>
                                            <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Method</p>
                                                <p className="text-lg font-mono">{lastestExecutionStatus.method_name || 'N/A'}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="mb-6">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(lastestExecutionStatus.status)}`}>
                                                    {lastestExecutionStatus.status === 'running' ? (
                                                        <span className="flex items-center">
                                                            <FaSpinner className="animate-spin mr-1" />
                                                            Running
                                                        </span>
                                                    ) : (
                                                        lastestExecutionStatus.status?.charAt(0).toUpperCase() + lastestExecutionStatus.status?.slice(1) || 'Unknown'
                                                    )}
                                                </span>
                                            </div>
                                            
                                            {lastestExecutionStatus.status === 'running' && (
                                                <div className="mt-4">
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                                        <div className="bg-blue-500 h-2.5 rounded-full animate-pulse" style={{ width: '75%' }}></div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {lastestExecutionStatus.start_time && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Started</p>
                                                    <p className="text-sm">{new Date(lastestExecutionStatus.start_time).toLocaleString()}</p>
                                                </div>
                                                {lastestExecutionStatus.end_time && (
                                                    <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
                                                        <p className="text-sm">{new Date(lastestExecutionStatus.end_time).toLocaleString()}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-6 text-center">
                                        <p className="text-gray-500 dark:text-gray-400">No execution history available.</p>
                                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                            Submit a job to see its status here
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            {/* Queue Position Section */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaList className="mr-2" />
                                        Queue Status
                                    </h2>
                                </div>
                                <div className="p-6">
                                    {queueStatus > 0 ? (
                                        <div className="text-center">
                                            <div className="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full px-6 py-3 mb-4">
                                                <p className="text-2xl font-bold">{queueStatus}</p>
                                                <p className="text-sm">jobs in queue ahead of you</p>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-400 mt-2">
                                                Your job will begin execution once these jobs complete. Estimated wait time varies based on job complexity.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <div className="inline-block bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full px-6 py-3 mb-4">
                                                <p className="text-2xl font-bold">0</p>
                                                <p className="text-sm">jobs in queue</p>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-400 mt-2">
                                                No jobs are currently in the queue. Your submission will be processed immediately if a spawner is available.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
    
                            {/* Spawner Availability */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mt-6">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaTachometerAlt className="mr-2" />
                                        Spawner Availability
                                    </h2>
                                </div>
                                <div className="p-6">
                                    {spawnerStatus.length === 0 ? (
                                        <div className="text-center py-4">
                                            <FaSpinner className="animate-spin h-8 w-8 mx-auto text-gray-400" />
                                            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading spawner status...</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-center mb-4">
                                                <div className="flex items-center mr-6">
                                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">Available</span>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">Busy</span>
                                                </div>
                                            </div>
                                            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                {spawnerStatus.map((spawner, index) => (
                                                    <li
                                                        key={index}
                                                        className={`p-3 rounded-lg text-center ${
                                                            spawner.status === 'idle'
                                                            ? 'bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-800'
                                                            : 'bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800'
                                                        }`}
                                                    >
                                                        <span className="font-medium text-gray-800 dark:text-gray-200">Spawner {index + 1}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
    
                {/* Logs Tab */}
                {activeTab === 'logs' && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                        <div className="bg-secondary-dark p-4 flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-white flex items-center">
                                <FaTerminal className="mr-2" />
                                Execution Logs
                            </h2>
                            {lastestExecutionStatus?.status === 'running' && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    <FaSpinner className="animate-spin mr-1" /> Live Output
                                </span>
                            )}
                        </div>
                        <div className="p-4">
                            <div className="bg-black text-white dark:bg-gray-900 p-4 rounded-md h-[600px] overflow-y-auto font-mono text-sm">
                                {latestLog.length > 0 ? (
                                    latestLog.slice().reverse().map((log, index) => (
                                        <pre key={index} className="mb-1 whitespace-pre-wrap break-words">{log}</pre>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                        <FaTerminal className="text-4xl mb-4" />
                                        <p>No logs to display.</p>
                                        <p className="text-sm mt-2">Run your code to see output here</p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                                <span>
                                    {lastestExecutionStatus?.status === 'running' 
                                        ? 'Log updates automatically every 5 seconds' 
                                        : latestLog.length > 0 
                                        ? `Last updated: ${new Date().toLocaleTimeString()}`
                                        : ''}
                                </span>
                                {latestLog.length > 0 && (
                                    <button
                                        onClick={fetchUserFiles}
                                        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center"
                                    >
                                        <FaFolder className="mr-1" />
                                        View Generated Files
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
    
                {/* Documentation Tab */}
                {activeTab === 'help' && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                        <div className="bg-secondary-dark p-4">
                            <h2 className="text-xl font-semibold text-white flex items-center">
                                <FaInfoCircle className="mr-2" />
                                Documentation & Help
                            </h2>
                        </div>
                        <div className="p-6">
                            <div className="space-y-8">
                                <section>
                                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-white">What are Spawners?</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                                        Spawners are specialized containers running participants' code for the competition.
                                        Each spawner has:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
                                        <li>A dedicated GPU for accelerated computation.</li>
                                        <li>CUDA 12.33, compatible with: torch==2.6, torchvision==0.21.0, torchaudio==2.6.0 and tensorflow[and-cuda]==2.18.0.</li>
                                        <li>Python 3.11 as the runtime environment.</li>
                                    </ul>
                                </section>
    
                                <section>
                                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-white">What are Requirements?</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                                        The "Requirements" field lists the Python dependencies your code needs. Specify them in this format:
                                    </p>
                                    <pre className="bg-gray-100 dark:bg-gray-750 p-4 rounded-md text-sm font-mono">
                                        numpy==1.25.1{'\n'}
                                        pandas==2.2.3{'\n'}
                                        scikit-learn==1.6.0
                                    </pre>
                                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                                        This ensures the spawner installs the exact library versions required by your script.
                                    </p>
                                </section>
    
                                <section>
                                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-white">How Does the Code Work?</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                                        Your code runs in a sandboxed environment:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
                                        <li>You can create, save, and read files in your relative directory.</li>
                                        <li>Write your Python logic in functions like <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">train()</code> or <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">predict()</code>.</li>
                                        <li>Specify the function to execute in the "Method name to run" field below.</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                                        Once submitted, your script runs on a spawner. Logs will appear in real-time, and you can explore files or download results.
                                    </p>
                                </section>
    
                                <section className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500">
                                    <h3 className="text-lg font-bold mb-2 text-blue-800 dark:text-blue-300 flex items-center">
                                        <FaLaptopCode className="mr-2" />
                                        Tips for Efficient Execution
                                    </h3>
                                    <ul className="list-disc pl-5 space-y-1 text-blue-700 dark:text-blue-400">
                                        <li>Save intermediate results to disk to resume work between runs</li>
                                        <li>Use multiprocessing for CPU-intensive tasks</li>
                                        <li>Use GPU acceleration with PyTorch or TensorFlow when possible</li>
                                        <li>Keep requirements minimal to speed up container startup time</li>
                                    </ul>
                                </section>
                            </div>
                        </div>
                    </div>
                )}
    
                {/* Reset Confirmation Dialog */}
{showConfirmReset && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Reset Script?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
                This will reset your code to the default example and clear any changes you've made. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
                <button
                    onClick={() => setShowConfirmReset(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={confirmReset}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                    Reset Script
                </button>
            </div>
        </div>
    </div>
)}

{/* File Explorer Modal */}
{showExplorer && (
    <FileExplorerModal files={userFiles} onClose={closeFileExplorer} />
)}
                            </div>
    </div>
);
}

export default Trainer;